import { FunctionComponent, useEffect, useState } from 'react';
import { PetBreakdownDetailTable } from './PetBreakdownDetailTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useMarketplace } from 'hooks';
import { Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import Header from '../../components/Header';
import styles from './PetBreakdown.module.scss';
import { PetBreakdownReportState, PetBreakdownReportStatus } from 'reducers/petBreakdownReport/PetBreakdownReportState';
import { getPetBreakdownReport } from 'actions/insights/clientsAndPets/petBreakdown/PetBreakdownReportActions';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';

interface PetBreakdownProps {
    description?: string;
}

export const PetBreakdown: FunctionComponent<PetBreakdownProps> = props => {
    const dispatch = useDispatch();

    const timeZone = useTimeZone();

    const petBreakdownLoading = useSelector<RootState, boolean>(state => state.petBreakdownReport.loading);
    const petBreakdownStatus = useSelector<RootState, PetBreakdownReportStatus>(
        state => state.petBreakdownReport.status
    );
    const petBreakdownReport = useSelector<RootState, PetBreakdownReportState>(state => state.petBreakdownReport);

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).startOf('month'),
        to: moment().tz(timeZone).endOf('month')
    });

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);

    const marketplace = useMarketplace();

    // export
    const petBreakdownCsvHeaders = ['Pet Type', 'Pet Breed', 'Total Bookings', 'Net Sales', 'Avg Ticket'];

    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const dataToExport =
        petBreakdownReport.report.pets?.map(item => [
            item.petType,
            item.petBreed,
            item.totalBookings.toString(),
            formatCurrency(item.netSales),
            formatCurrency(item.avgTicket)
        ]) || [];

    const exportFile = {
        name: `Pet Breakdown - ${marketplace.basics.businessName}.csv`,
        dataInMemory: true,
        dataToExport: dataToExport,
        headers: petBreakdownCsvHeaders
    };

    const calendar = {
        optionSelected: 'This month'
    };

    const [retryAttempt, setRetryAttempt] = useState(0);

    const fetchData = (p: Period | null, nextPage: number, nextSize: number) => {
        dispatch(getPetBreakdownReport(p?.from.format('YYYY/MM/DD'), p?.to.format('YYYY/MM/DD'), nextPage, nextSize));
    };

    useEffect(() => {
        fetchData(period, page, size);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (petBreakdownStatus === PetBreakdownReportStatus.Error) {
            setRetryAttempt(prev => prev + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petBreakdownStatus]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        setPage(1);
        if (allTime) {
            setPeriod(null);
            fetchData(null, 1, size);
        } else {
            const nextPeriod = {
                from: dateFilter?.from || moment(),
                to: dateFilter?.to || moment()
            };
            setPeriod(nextPeriod);
            fetchData(nextPeriod, 1, size);
        }
    };

    const handlePageChange = (nextPage: number) => {
        setPage(nextPage);
        fetchData(period, nextPage, size);
    };

    const handleSizeChange = (nextSize: number) => {
        setSize(nextSize);
        setPage(1);
        fetchData(period, 1, nextSize);
    };

    return (
        <div
            className={styles.container}
            key={retryAttempt}
        >
            <div>
                <span className={styles.description}>{props.description}</span>
            </div>
            <div className={styles.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>
            <PetBreakdownDetailTable
                loading={petBreakdownLoading}
                data={petBreakdownReport.report.pets}
            />

            {petBreakdownReport.report.pets.length > 0 && (
                <div className={styles.paginationWrap}>
                    <Pagination
                        page={petBreakdownReport.report.meta.page ?? page}
                        totalPages={petBreakdownReport.report.meta.totalPages ?? 1}
                        size={petBreakdownReport.report.meta.perPage ?? size}
                        isLoading={petBreakdownLoading}
                        onPageChange={handlePageChange}
                        onSizeChange={handleSizeChange}
                    />
                </div>
            )}
        </div>
    );
};

export default PetBreakdown;
